import React from 'react';
import { Typography, Box, Tooltip } from '@mui/material';
import TimeSince from 'components/shared/timeSince';
import clsx from 'clsx';
import moment from 'moment';
import StaffIndicator from 'components/shared/staffIndicator';
import useTranslation from 'hooks/useTranslation';
import { AccessTimeRounded, CheckCircle, Error } from '@mui/icons-material';
import useStyles from './message-styles';

interface MessageProps {
  message: Message;
  sender: string;
  conversation: Conversation;
  isMyMessage: boolean;
  showSender: boolean;
  showTimestamp: boolean;
  timezone: string;
  isDelivering: boolean;
  isAssetConvo: boolean;
  sentMessages: Message[];
}

const MessageView = ({
  message,
  sender,
  conversation,
  isMyMessage,
  showSender,
  showTimestamp,
  timezone,
  isDelivering,
  isAssetConvo,
  sentMessages,
  ...rest
}: MessageProps): JSX.Element => {
  const classes = useStyles();
  const time = moment.unix(message.timestamp).tz(timezone);
  const t = useTranslation('pages.messaging');
  const sentMessageStatus = message.realId ? sentMessages[parseInt(message.realId, 10)].status : undefined;
  const sendStatus = sentMessageStatus || message.status || (isDelivering && 'delivering') || null;

  return (
    <div className={clsx(classes.messageWrapper, { [classes.myMessageWrapper]: isMyMessage })} {...rest} key={message.id} id={message.id}>
      {showTimestamp && (
        <div className={classes.timestampWrapper}>
          {/*
            // @ts-ignore */}
          <TimeSince time={time} Component={Box} className={classes.timestamp} tooltip tooltipFormat="HH:mm:ss, dddd D MMM YYYY z" />
        </div>
      )}
      {showSender && (
        <Typography className={classes.senderName}>
          {sender}
          <StaffIndicator user={message.sender} overrideStyle={{ width: '1.6rem', height: '1.6rem' }} />
        </Typography>
      )}
      <div className={classes.messageBubble}>
        <Tooltip title={time.format('HH:mm:ss, dddd D MMM YYYY z')} placement="bottom" arrow>
          <Typography className={clsx(classes.messageText, { [classes.myMessageText]: isMyMessage })}>
            {message.content}
          </Typography>
        </Tooltip>
      </div>
      {isMyMessage && isAssetConvo && sendStatus && (
        <Tooltip title={t(sendStatus)}>
          {sendStatus === 'sent'
            ? <CheckCircle className={classes.statusIcon} />
            : sendStatus === 'failed'
              ? <Error className={classes.statusIcon} />
              : <AccessTimeRounded className={classes.statusIcon} />}
        </Tooltip>
      )}
    </div>
  );
};

export default MessageView;
